import { useMemo, useState, useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { router } from '@inertiajs/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'sonner';

import { formatFileSize, FormField } from '../../../../components/FormField';
import { useInertiaForm } from '../../../../hooks/useInertiaForm';
import { urlsListIsValid, urlsToFiles } from '../../../../utils/urls';
import { pluralize } from '../../../../utils/strings';
import Toast from '../Toast';
import CompressionModal from './CompressionModal';
import FileRow from './FileRow';
import {
  type FileMeta,
  defaultMeta,
  stripExtension,
  formatToExt,
  originalExt,
  compressImage,
} from './imageCompression';

const ACCEPTED_TYPES = [
  'image/*', 'audio/*', 'video/*',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export default function NewAttachmentForm() {
  const [filesMeta, setFilesMeta] = useState<FileMeta[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalIndex, setModalIndex] = useState<number | null>(null);

  const { formMethods, isSubmitting, onCancel } = useInertiaForm({
    key: 'admin_attachments',
    serverData: { files: [] as File[], files_urls: '' },
    onPropChange: (data) => data,
    options: { url: '/admin/attachments', method: 'post' },
  });

  const { watch, formState: { isDirty }, setValue, handleSubmit } = formMethods;

  const files: File[] = watch('files') || [];
  const filesUrls: string = watch('files_urls') || '';

  const addFiles = (newFiles: File[]) => {
    setValue('files', [...files, ...newFiles], { shouldDirty: true });
    setFilesMeta((prev) => [...prev, ...newFiles.map(defaultMeta)]);
  };

  const removeFile = (index: number) => {
    setValue('files', files.filter((_, i) => i !== index), { shouldDirty: true });
    setFilesMeta((prev) => prev.filter((_, i) => i !== index));
  };

  const updateMeta = (index: number, patch: Partial<FileMeta>) => {
    setFilesMeta((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  // Recompute compressedSize for entries whose compress settings changed
  const compressKey = filesMeta.map((m) => `${m.compress}-${m.quality}-${m.format}`).join('|');
  const sizeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (sizeDebounceRef.current) clearTimeout(sizeDebounceRef.current);
    sizeDebounceRef.current = setTimeout(async () => {
      const updates = await Promise.all(
        filesMeta.map(async (meta, i) => {
          const file = files[i];
          if (!file || !meta.compress || !file.type.startsWith('image/')) return null;
          try {
            const out = await compressImage(file, meta.quality / 100, meta.format);
            return { i, size: out.size };
          } catch {
            return null;
          }
        }),
      );
      setFilesMeta((prev) =>
        prev.map((m, i) => {
          const u = updates.find((u) => u?.i === i);
          if (u) return { ...m, compressedSize: u.size };
          if (!m.compress) return { ...m, compressedSize: null };
          return m;
        }),
      );
    }, 400);
  }, [compressKey, files.length]);

  const urlsAreValid = useMemo(() => urlsListIsValid(filesUrls), [filesUrls]);

  const [fetchingFiles, setFetchingFiles] = useState(false);
  const fetchFiles = async () => {
    setFetchingFiles(true);
    try {
      const newFiles = await urlsToFiles(filesUrls);
      addFiles(newFiles);
      setValue('files_urls', '');
      toast.custom((id) => (
        <Toast id={id} level="success" message={`${newFiles.length} ${pluralize(newFiles.length, 'file')} fetched successfully.`} />
      ));
    } catch (error) {
      toast.custom((id) => <Toast id={id} level="error" message={String(error)} />);
    } finally {
      setFetchingFiles(false);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const active = document.activeElement;
    if (active?.tagName === 'TEXTAREA' && active.getAttribute('name') === 'files_urls') return;
    for (const item of event.clipboardData.items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          event.preventDefault();
          addFiles([file]);
          toast.custom((id) => <Toast id={id} level="success" message={`File "${file.name}" added.`} />);
        }
      }
    }
  };

  const handleSubmitWithProcessing = async (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(async (data) => {
      setIsProcessing(true);
      try {
        const processedFiles = await Promise.all(
          (data.files as File[]).map(async (file, index) => {
            const meta = filesMeta[index];
            let out = file;

            if (meta?.compress && file.type.startsWith('image/')) {
              out = await compressImage(file, meta.quality / 100, meta.format);
            }

            const ext = meta?.compress && file.type.startsWith('image/')
              ? formatToExt(meta.format)
              : originalExt(file.name);
            const finalName = (meta?.name || stripExtension(file.name)) + ext;

            return finalName !== out.name ? new File([out], finalName, { type: out.type }) : out;
          }),
        );

        router.post(
          '/admin/attachments',
          { admin_attachments: { files: processedFiles } },
          { preserveScroll: true, onFinish: () => setIsProcessing(false) },
        );
      } catch (err) {
        setIsProcessing(false);
        toast.custom((id) => <Toast id={id} level="error" message={`Processing failed: ${String(err)}`} />);
      }
    })();
  };

  const loading = isSubmitting || fetchingFiles || isProcessing;
  const hasFiles = files.length > 0;
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  return (
    <FormProvider {...formMethods}>
      {modalIndex !== null && filesMeta[modalIndex] && (
        <CompressionModal
          meta={filesMeta[modalIndex]}
          file={files[modalIndex]}
          onSave={(patch) => updateMeta(modalIndex, patch)}
          onClose={() => setModalIndex(null)}
        />
      )}

      <form
        onSubmit={handleSubmitWithProcessing}
        onPaste={handlePaste}
        style={{ width: '100%', display: 'flex', flexDirection: 'column', border: '1px solid #ddd', borderRadius: '8px', height: '100%' }}
      >
        {/* Header */}
        <div style={{
          width: '100%', backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd',
          padding: '4px 8px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>New Attachment</div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {isDirty && (
              <button type="button" onClick={onCancel} disabled={loading} className="btn btn-sm">Cancel</button>
            )}
            <button type="submit" className="btn btn-sm btn-success" disabled={loading || !hasFiles}>
              {isProcessing ? 'Processing...' : isSubmitting ? 'Saving...' : 'Save'}
              <FontAwesomeIcon icon={faCheck} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ backgroundColor: 'white', padding: '4px', height: '100%', overflow: 'auto' }}>

          {/* URL fetch */}
          <FormField.Container style={{ backgroundColor: 'var(--color-base-300)', padding: '4px' }}>
            <FormField.Field
              name="files_urls"
              label="Fetch files from URLs"
              type="textarea"
              registerProps={{ required: false }}
              inputProps={{ placeholder: 'https://example.com/file1.jpg\nhttps://example.com/file2.pdf' }}
            />
            <div
              className="tooltip tooltip-right w-fit"
              data-tip={!filesUrls ? 'Enter URLs to fetch files' : !urlsAreValid ? 'One or more URLs are invalid' : 'Fetch files from the provided URLs'}
            >
              <button className="btn btn-primary btn-sm" disabled={loading || !urlsAreValid} onClick={fetchFiles} type="button">
                Fetch files
              </button>
            </div>
          </FormField.Container>

          <div className="divider text-sm my-2">OR</div>

          {/* File picker */}
          <FormField.Container style={{ backgroundColor: 'var(--color-base-300)', padding: '4px' }}>
            <FormField.MultiFileInput
              name="files"
              acceptedTypes={ACCEPTED_TYPES}
              maxSingleFileSize={100 * 1024 * 1024}
              maxTotalSize={500 * 1024 * 1024}
              maxFilesCount={50}
              label="Upload Files"
              registerProps={{ required: false }}
              onFilesAdded={addFiles}
            />
          </FormField.Container>

          <div className="divider text-sm my-2">Limits</div>

          <div className="text-xs text-base-content/70 flex flex-col gap-1 mb-2">
            <div>Max single file: {formatFileSize(10 * 1024 * 1024)}</div>
            <div>Max files count: 10 (used: {files.length})</div>
            <div>Max total: {formatFileSize(50 * 1024 * 1024)} (used: {formatFileSize(totalSize)})</div>
            <div>Accepted types: {ACCEPTED_TYPES.join(', ')}</div>
          </div>

          <div className="divider text-sm my-2">Inputted Files ({files.length})</div>

          {hasFiles && (
            <div className="flex flex-col gap-2 mb-4">
              {files.map((file, index) => (
                <FileRow
                  key={`${file.name}-${index}`}
                  file={file}
                  meta={filesMeta[index] ?? defaultMeta(file)}
                  onUpdate={(patch) => updateMeta(index, patch)}
                  onRemove={() => removeFile(index)}
                  onOpenModal={() => setModalIndex(index)}
                />
              ))}
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  );
}

